import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  TextField,
  Button,
  Tooltip,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  IconButton,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { fetchLocationsByCompany } from 'services/api/locationService';

const ControlBar = ({
  viewMode = 'company',
  onViewModeChange,
  onCompanyChange,
  onLocationChange,
  selectedCompany: selectedCompanyName,
  selectedLocation: selectedLocationName,
  selectedGuardId: selectedGuardIdProp,
  onGuardChange,
  guards = [],
  companies = [],
  onAddNewPatrol,
  onExport,
  isLoadingSchedule = false,
  isMobile = false,
}) => {
  const { notifyError } = useNotificationProvider();

  // Local state for controlled inputs (stores IDs for company/location/guard)
  const [company, setCompany] = useState('');
  const [location, setLocation] = useState('');
  const [guard, setGuard] = useState(null);

  // Locations fetched based on selected company
  const [availableLocations, setAvailableLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Track if we're currently fetching locations to handle race conditions
  const locationFetchIdRef = useRef(0);

  // Fetch locations when company changes
  useEffect(() => {
    if (!company) {
      setAvailableLocations([]);
      return;
    }

    // Increment fetch ID to track this specific request
    const fetchId = ++locationFetchIdRef.current;
    setLoadingLocations(true);

    const loadLocations = async () => {
      try {
        const locationsData = await fetchLocationsByCompany(company);
        // Only update if this is still the current request
        if (fetchId === locationFetchIdRef.current) {
          setAvailableLocations(locationsData);
        }
      } catch (error) {
        console.error('[ControlBar] Failed to fetch locations:', { companyId: company, error });
        if (fetchId === locationFetchIdRef.current) {
          setAvailableLocations([]);
          notifyError('Failed to load locations. Please try again.');
        }
      } finally {
        if (fetchId === locationFetchIdRef.current) {
          setLoadingLocations(false);
        }
      }
    };

    loadLocations();
  }, [company, notifyError]);

  // Sync internal company state when parent's selectedCompanyName changes
  // Note: We intentionally exclude 'company' from deps to avoid loops
  useEffect(() => {
    if (selectedCompanyName && companies.length > 0) {
      const matchingCompany = companies.find((c) => c.name === selectedCompanyName);
      if (matchingCompany) {
        setCompany(matchingCompany.id);
      }
    } else if (!selectedCompanyName) {
      setCompany('');
    }
  }, [selectedCompanyName, companies]);

  // Sync internal location state when parent's selectedLocationName changes
  // Note: We intentionally exclude 'location' from deps to avoid loops
  useEffect(() => {
    if (selectedLocationName && availableLocations.length > 0) {
      const matchingLocation = availableLocations.find((l) => l.name === selectedLocationName);
      if (matchingLocation) {
        setLocation(matchingLocation.id);
      }
    } else if (!selectedLocationName) {
      setLocation('');
    }
  }, [selectedLocationName, availableLocations]);

  // Sync internal guard state when parent's selectedGuardId changes
  // Note: We intentionally exclude 'guard' from deps to avoid loops
  useEffect(() => {
    if (selectedGuardIdProp && guards.length > 0) {
      const matchingGuard = guards.find((g) => g.id === selectedGuardIdProp);
      if (matchingGuard) {
        setGuard(matchingGuard.id);
      }
    } else if (!selectedGuardIdProp) {
      setGuard(null);
    }
  }, [selectedGuardIdProp, guards]);

  const handleViewModeChange = useCallback(
    (event, newMode) => {
      if (newMode !== null) {
        onViewModeChange?.(newMode);
      }
    },
    [onViewModeChange]
  );

  const handleGuardChange = useCallback(
    (guardId) => {
      setGuard(guardId);
      onGuardChange?.(guardId);
    },
    [onGuardChange]
  );

  const handleCompanyChange = useCallback(
    (e) => {
      const selectedId = e.target.value;
      setCompany(selectedId);
      setLocation(''); // Clear location when company changes

      // Find the company name and pass both name and ID to parent
      // Parent's handleCompanyChange will also clear location state
      const selectedCompanyObj = companies.find((c) => c.id === selectedId);
      onCompanyChange?.(selectedCompanyObj?.name || '', selectedId || null);
    },
    [companies, onCompanyChange]
  );

  const handleLocationChange = useCallback(
    (e) => {
      const selectedId = e.target.value;
      setLocation(selectedId);

      // Find the location name and pass both name and ID to parent
      const selectedLocationObj = availableLocations.find((l) => l.id === selectedId);
      onLocationChange?.(selectedLocationObj ? selectedLocationObj.name : '', selectedId || null);
    },
    [availableLocations, onLocationChange]
  );

  const handleAddPatrol = useCallback(() => {
    // Get company and location names to autofill in modal
    const selectedCompanyObj = companies.find((c) => c.id === company);
    const selectedLocationObj = availableLocations.find((l) => l.id === location);

    onAddNewPatrol?.({
      CustomerName: selectedCompanyObj ? selectedCompanyObj.name : '',
      AddressName: selectedLocationObj ? selectedLocationObj.name : '',
      ContactId: viewMode === 'guard' ? guard : null,
      ContactName: viewMode === 'guard' ? (guards.find((g) => g.id === guard)?.name || '') : '',
    });
  }, [companies, availableLocations, company, location, guard, viewMode, onAddNewPatrol]);

  const handleClearFilters = useCallback(() => {
    // Clear local state
    setCompany('');
    setLocation('');
    setGuard(null);
    setAvailableLocations([]);

    // Notify parent to clear its state
    if (viewMode === 'company') {
      onCompanyChange?.('', null);
      onLocationChange?.('', null);
    } else {
      onGuardChange?.(null);
    }
  }, [viewMode, onCompanyChange, onLocationChange, onGuardChange]);

  // Check if any filters are active
  const hasActiveFilters = viewMode === 'company' ? (company || location) : guard;

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        padding: { xs: 1.5, sm: 2 },
        height: '100%',
      }}
    >
      {/* Title */}
      <Box sx={{ marginBottom: { xs: 1.5, sm: 2 } }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{ fontWeight: 600 }}
        >
          CANdispatch Scheduler
        </Typography>
      </Box>

      {/* Control Row */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1.5, sm: 2 },
          alignItems: isMobile ? 'stretch' : 'center',
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {/* View Mode Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          fullWidth={isMobile}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: isMobile ? 1 : 2,
              flex: isMobile ? 1 : 'none',
            },
          }}
        >
          <ToggleButton value="company" aria-label="company view">
            <BusinessIcon sx={{ mr: 0.75, fontSize: 20 }} />
            Company
          </ToggleButton>
          <ToggleButton value="guard" aria-label="guard view">
            <PersonIcon sx={{ mr: 0.75, fontSize: 20 }} />
            Guard
          </ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'company' ? (
          <>
            {/* Company Select with Search */}
            <Autocomplete
              value={companies.find((c) => c.id === company) || null}
              onChange={(_, newValue) => {
                const selectedId = newValue?.id || '';
                setCompany(selectedId);
                setLocation(''); // Clear location when company changes
                const selectedCompanyObj = companies.find((c) => c.id === selectedId);
                onCompanyChange?.(selectedCompanyObj?.name || '', selectedId || null);
              }}
              options={companies}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  placeholder="Search companies..."
                  size="small"
                />
              )}
              sx={{
                minWidth: isMobile ? 'auto' : 200,
                width: isMobile ? '100%' : 'auto',
              }}
              size="small"
            />

            {/* Location Select with Search */}
            <Tooltip
              title={!company ? 'Select a company first' : ''}
              disableHoverListener={!!company}
            >
              <div style={{ width: isMobile ? '100%' : 'auto' }}>
                <Autocomplete
                  value={availableLocations.find((l) => l.id === location) || null}
                  onChange={(_, newValue) => {
                    const selectedId = newValue?.id || '';
                    setLocation(selectedId);
                    const selectedLocationObj = availableLocations.find((l) => l.id === selectedId);
                    onLocationChange?.(selectedLocationObj ? selectedLocationObj.name : '', selectedId || null);
                  }}
                  options={availableLocations}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={!company}
                  loading={loadingLocations}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      placeholder={company ? 'Search locations...' : 'Select company first'}
                      size="small"
                    />
                  )}
                  sx={{
                    minWidth: isMobile ? 'auto' : 200,
                    width: isMobile ? '100%' : 'auto',
                  }}
                  size="small"
                />
              </div>
            </Tooltip>

            {/* Loading indicator when fetching schedule */}
            {isLoadingSchedule && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                {!isMobile && (
                  <Typography variant="body2" color="text.secondary">
                    Loading schedule...
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : (
          /* Guard Select with Search */
          <>
            <Autocomplete
              value={guards.find((g) => g.id === guard) || null}
              onChange={(_, newValue) => {
                const selectedId = newValue?.id || null;
                setGuard(selectedId);
                onGuardChange?.(selectedId);
              }}
              options={guards}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Guard"
                  placeholder="Search guards..."
                  size="small"
                />
              )}
              sx={{
                minWidth: isMobile ? 'auto' : 200,
                width: isMobile ? '100%' : 'auto',
              }}
              size="small"
            />

            {/* Loading indicator when fetching schedule */}
            {isLoadingSchedule && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                {!isMobile && (
                  <Typography variant="body2" color="text.secondary">
                    Loading schedule...
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}

        {/* Spacer to push buttons to the right - hidden on mobile */}
        {!isMobile && <Box sx={{ flexGrow: 1 }} />}

        {/* Action buttons row */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            width: isMobile ? '100%' : 'auto',
            mt: isMobile ? 0.5 : 0,
          }}
        >
          {/* Export Button */}
          <Tooltip title="Export schedule to CSV">
            <IconButton
              onClick={onExport}
              sx={{ color: 'text.secondary' }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>

          {/* Clear Filters Button */}
          <Tooltip title="Clear calendar filters">
            <span>
              <IconButton
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{ color: 'text.secondary' }}
              >
                <FilterAltOffIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Add Patrol Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddPatrol}
            startIcon={<AddIcon />}
            sx={{
              flex: isMobile ? 1 : 'none',
            }}
          >
            Add Patrol
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

ControlBar.propTypes = {
  viewMode: PropTypes.oneOf(['company', 'guard']),
  onViewModeChange: PropTypes.func,
  onCompanyChange: PropTypes.func,
  onLocationChange: PropTypes.func,
  selectedCompany: PropTypes.string,
  selectedLocation: PropTypes.string,
  selectedGuardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onGuardChange: PropTypes.func,
  guards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  onAddNewPatrol: PropTypes.func,
  onExport: PropTypes.func,
  isLoadingSchedule: PropTypes.bool,
  isMobile: PropTypes.bool,
};

// Memoize component to prevent unnecessary re-renders when parent state changes
export default memo(ControlBar);
